import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, Button } from "@mui/material";
import SubtitleEditor from "./SubtitleEditor";
import { getStorage, ref, getDownloadURL } from "firebase/storage";

// SRT parser
function timeToSeconds(time) {
    const [h, m, rest] = time.split(":");
    const [s, ms] = rest.split(",");
    return (
        parseInt(h) * 3600 +
        parseInt(m) * 60 +
        parseInt(s) +
        parseInt(ms) / 1000
    );
}

function parseSRT(text) {
    const blocks = text.replace(/\r\n/g, "\n").trim().split("\n\n");
    const result = [];

    for (const block of blocks) {
        const lines = block.split("\n").map((l) => l.trim());
        if (lines.length >= 2) {
            const timeLine = lines.find((l) => l.includes("-->"));
            if (!timeLine) continue;

            const [startRaw, endRaw] = timeLine.split("-->");
            const start = timeToSeconds(startRaw.trim());
            const end = timeToSeconds(endRaw.trim());
            const textLines = lines.slice(lines.indexOf(timeLine) + 1);
            const text = textLines.join(" ").replace(/<\/?[^>]+(>|$)/g, "").trim();

            result.push({ start, end, text });
        }
    }
    return result;
}

/* ---------------- MediaPlayer ---------------- */
const MediaPlayer = ({ path, contentType }) => {
    const [url, setUrl] = useState("");

    useEffect(() => {
        if (!path) return;
        const storage = getStorage();
        const storageRef = ref(storage, path);

        getDownloadURL(storageRef)
            .then((downloadURL) => setUrl(downloadURL))
            .catch((err) => console.error("Error fetching media URL:", err));
    }, [path]);

    if (!url) return <div>Loading media...</div>;

    if (contentType === "podcast") {
        return <audio controls style={{ width: "100%" }} src={url} />;
    }

    return <video controls style={{ width: "100%", maxHeight: 200 }} src={url} />;
};

/* ---------------- Main Component ---------------- */
const MediaPreviewAndSubtitle = ({ values, setFieldValue, loading }) => {
    const languages = Object.keys(values.mediaURLs); // ['en','tr']

    if (!["video", "podcast"].includes(values.contentType)) return null;

    const isVideo = values.contentType === "video";
    const isPodcast = values.contentType === "podcast";

    // video için tek veya iki video kontrolü
    const urls = Object.values(values.mediaURLs);
    const isSingleVideo = isVideo && urls.every(url => url === urls[0]);

    const handleSrtUpload = (e, lang) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const parsed = parseSRT(evt.target.result);
                setFieldValue(`subtitles.${lang}`, JSON.stringify(parsed));
            } catch {
                alert("Invalid SRT file format");
            }
        };
        reader.readAsText(file);
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Media Preview & Subtitles
            </Typography>
            <Grid container spacing={3}>
                {/* Podcast */}
                {isPodcast &&
                    languages.map((lang) => (
                        <Grid item xs={12} sm={6} key={lang}>
                            <Typography variant="subtitle1">{lang.toUpperCase()} Preview</Typography>
                            <MediaPlayer path={values.mediaURLs[lang]} contentType="podcast" />
                        </Grid>
                    ))}

                {/* Video Preview */}
                {isVideo && (
                    <>
                        {isSingleVideo ? (
                            <Grid item xs={12}>
                                <Typography variant="subtitle1">Preview</Typography>
                                <MediaPlayer path={values.mediaURLs[languages[0]]} contentType="video" />
                            </Grid>
                        ) : (
                            languages.map((lang) => (
                                <Grid item xs={12} sm={6} key={lang}>
                                    <Typography variant="subtitle1">{lang.toUpperCase()} Preview</Typography>
                                    <MediaPlayer path={values.mediaURLs[lang]} contentType="video" />
                                </Grid>
                            ))
                        )}

                        {/* Video subtitles */}
                        {languages.map((lang) => (
                            <Grid item xs={12} sm={6} key={lang}>
                                <Typography variant="subtitle2">Subtitles ({lang.toUpperCase()})</Typography>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ mb: 1 }}
                                    disabled={loading}
                                >
                                    Upload SRT
                                    <input
                                        type="file"
                                        accept=".srt"
                                        hidden
                                        onChange={(e) => handleSrtUpload(e, lang)}
                                    />
                                </Button>
                                <SubtitleEditor
                                    value={values.subtitles?.[lang] || ""}
                                    onChange={(subs) => setFieldValue(`subtitles.${lang}`, subs)}
                                    disabled={loading}
                                />
                            </Grid>
                        ))}
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default MediaPreviewAndSubtitle;
